// Then the boundary gets duplicated, and moves to the side so we have two identical boundaries
// right next to each other (they'll need to shrink to fit)… Then the boundary gets duplicated again
// and moves between/below the first two to form a triangle… Each boundary should get labelled with
// the layer name.
import { flipDelayFraction, type AnimCell } from "./mapSoilAnimation";
import { RELATIVE_PLANES } from "./animPlanes";

/*
 *
 * The three inputs, in order, each labelled on its own panel.
 */
export const RELATIVE_PANELS = [
  { key: RELATIVE_PLANES.soil.key, label: RELATIVE_PLANES.soil.label },
  { key: RELATIVE_PLANES.water.key, label: RELATIVE_PLANES.water.label },
  {
    key: RELATIVE_PLANES.yield.key,
    label: RELATIVE_PLANES.yield.label,
    fallback: RELATIVE_PLANES.yieldFallback.key,
  },
] as const;

/*
 *
 * THE PANEL COUNT GROWS WITH THE BEAT — one, then two, then three — because sequence is the
 * field being duplicated, not three panels sitting there waiting to be filled.
 */
export function relativeStateAt(
  t: number,
  spanMs: number,
): { count: 1 | 2 | 3; revealing: number; within: number } {
  const per = spanMs / RELATIVE_PANELS.length;
  const i = Math.min(
    RELATIVE_PANELS.length - 1,
    Math.max(0, Math.floor(t / per)),
  );
  return {
    count: (i + 1) as 1 | 2 | 3,
    revealing: i,
    within: Math.min(1, (t - i * per) / per),
  };
}

/*
 *
 * The shrink is not cosmetic: the panels have to fit WITHOUT cropping, or the "identical
 * boundaries" claim breaks the moment one of them is clipped.
 */
export function panelBox(
  i: number,
  count: 1 | 2 | 3,
  vh: number,
): { scale: number; tx: number; ty: number } {
  const centres: Record<number, [number, number][]> = {
    1: [[0.5, 0.5]],
    2: [
      [0.26, 0.5],
      [0.74, 0.5],
    ],
    3: [
      [0.27, 0.3],
      [0.73, 0.3],
      [0.5, 0.74],
    ],
  };
  const scale = count === 1 ? 1 : count === 2 ? 0.46 : 0.42;
  const [cx, cy] = centres[count]![i] ?? [0.5, 0.5];
  return { scale, tx: cx * 100 - scale * 50, ty: cy * vh - (scale * vh) / 2 };
}

/* * Has this tile flipped yet, within a reveal that runs 0→1? The same spiral as every other animation. */
export function panelTileIn(cell: AnimCell, within: number): boolean {
  return within >= flipDelayFraction(cell);
}

/*
 *
 * THE YIELD PANEL FALLS BACK TO NDVI PEAK, and only that one has a fallback. chose the MODELLED
 * yield precisely because it exists on every field, so a field missing it is an oddity rather than
 * the normal case — but showing an empty third panel captioned "Simulated Yield" would claim a
 * layer that is not there, and the NDVI stand-in is the documented substitute.
 */
export function panelPlaneIndex(keys: readonly string[], i: number): number {
  const p = RELATIVE_PANELS[i]!;
  const at = keys.indexOf(p.key);
  if (at >= 0) return at;
  return "fallback" in p && p.fallback ? keys.indexOf(p.fallback) : -1;
}
